import React from "react";
import ReactECharts from "echarts-for-react";
import { groupSeriesXY, toTimestamp, formatDateFr } from "../../utils/echartsSeries.js";
import { YEAR_COLORS } from "../../constants.js";

// Valeur exacte à un instant donné (timestamp), par interpolation linéaire
// entre les deux points encadrants. Renvoie null si l'instant est hors de
// la plage couverte par les données (ex: avant le semis).
function valueAtDate(points, targetTime) {
  if (points.length === 0) return null;
  const first = new Date(points[0].date).getTime();
  const last = new Date(points[points.length - 1].date).getTime();
  if (targetTime < first || targetTime > last) return null;
  for (let i = 0; i < points.length - 1; i++) {
    const t1 = new Date(points[i].date).getTime();
    const t2 = new Date(points[i + 1].date).getTime();
    if (targetTime >= t1 && targetTime <= t2) {
      const frac = t2 === t1 ? 0 : (targetTime - t1) / (t2 - t1);
      return points[i].valeur + frac * (points[i + 1].valeur - points[i].valeur);
    }
  }
  return null;
}

function formatMonthLabel(monthIndex) {
  return `01/${String(monthIndex + 1).padStart(2, "0")}`;
}

export default function IrrigationJournalierChart({ data, xMax }) {
  const groups = groupSeriesXY(data, {
    xKey: "date_affichage",
    groupKey: "Years",
    valueKey: "irr_moy",
  });

  const pointsByYear = {};
  const series = groups.map(({ group, points }) => {
    const color = YEAR_COLORS[group] || "#888";
    const pointsFiltres = xMax != null ? points.filter((p) => toTimestamp(p.x) <= xMax) : points;
    const pts = pointsFiltres.map((p) => ({ date: p.x, valeur: p.y }));
    pointsByYear[group] = pts;
    return {
      name: String(group),
      type: "line",
      symbol: "none",
      lineStyle: { width: 1.5, color },
      itemStyle: { color },
      data: pts.map((p) => [toTimestamp(p.date), p.valeur]),
    };
  });

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        params.forEach((p) => {
          html += `${p.marker} ${p.seriesName}: ${p.value[1]} mm/j<br/>`;
        });
        return html;
      },
    },
    legend: { bottom: 0 },
    grid: { left: 60, right: 20, top: 20, bottom: 50, containLabel: true },
    xAxis: { type: "time", max: xMax ?? undefined, axisLabel: { formatter: formatDateFr } },
    yAxis: { type: "value", name: "Irrigation (mm/j)", nameLocation: "middle", nameGap: 45 },
    series,
  };

  // ---- tableau des écarts (%) vs l'horizon le plus ancien, au 1er de chaque mois ----
  const anneesDisponibles = Object.keys(pointsByYear).map(Number).sort((a, b) => a - b);
  const anneeBase = anneesDisponibles[0];
  const autresAnnees = anneesDisponibles.slice(1);
  const pointsBase = pointsByYear[anneeBase] || [];

  const moisCandidats = Array.from({ length: 12 }, (_, m) => new Date(2025, m, 1).getTime());
  const monthsFinal = [];
  moisCandidats.forEach((t, m) => {
    const base = valueAtDate(pointsBase, t);
    if (base != null) monthsFinal.push({ m, t, base });
  });

  const ecartLignes = autresAnnees.map((year) => {
    const points = pointsByYear[year];
    const valeurs = monthsFinal.map(({ t, base }) => {
      const val = valueAtDate(points, t);
      if (val == null || base === 0) return null;
      return Math.round(((val - base) / base) * 1000) / 10;
    });
    return { year, valeurs };
  });

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: "2 1 0" }}>
        <ReactECharts option={option} style={{ height: 350 }} notMerge lazyUpdate />
      </div>

      {monthsFinal.length > 0 && (
        <div style={{ flex: "1 1 0", overflowX: "auto" }}>
          <table className="diagoeau-data-table diagoeau-table-centered">
            <thead>
              <tr>
                <th></th>
                {ecartLignes.map((row) => (
                  <th key={row.year}>{row.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthsFinal.map(({ m }, i) => (
                <tr key={m}>
                  <td>{formatMonthLabel(m)}</td>
                  {ecartLignes.map((row) => {
                    const v = row.valeurs[i];
                    return (
                      <td key={row.year}>
                        {v == null ? "—" : `${v > 0 ? "+" : ""}${v.toLocaleString("fr-FR")} %`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}